import React, { useState } from 'react';
import P, { InferProps } from 'prop-types';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LoadingButton from '@mui/lab/LoadingButton';
import FormHelperText from '@mui/material/FormHelperText';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import TextInput from '../../../common/fields/text-field';
import { useStringsContext } from '../../../../contexts/strings';
import adminApi from '../../../../api/admin-api';
import { useUserAuth } from '../../../../contexts/auth';

export default function AddUserDialog({
  isOpen,
  onClose,
}: InferProps<typeof AddUserDialog.propTypes>) {
  const [email, setEmail] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const { currentUser } = useUserAuth();

  const handleSave = async () => {
    if (!email || !isEmailValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      setIsError(false);

      const response = await adminApi.post({
        path: `/users/add-backend-user`,
        auth: await currentUser?.getIdToken(),
        body: { email },
      });
      const responseData = await response.json();

      setIsSubmitting(false);

      const isError = responseData.error;
      if (isError) {
        console.error(responseData.error);
        setIsError(true);
        throw new Error();
      }

      if (!isError) {
        onClose();
      }
    } catch (error) {
      setIsSubmitting(false);
      enqueueSnackbar(strings.messages.saveError, {
        variant: 'error',
      });
    }
  };

  const handleInputChange = async (_: unknown, value: string, validation: any) => {
    const emailValidation: boolean = await validation.isValid(value);

    setIsEmailValid(emailValidation);

    if (emailValidation) {
      setEmail(value);
    }
  };

  const handleValidation = () => {
    // do nothing
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth>
      <DialogTitle id="edit-permissions-dialog">{strings.usersAndPermissions.addUser}</DialogTitle>
      <DialogContent dividers>
        <TextInput
          field={{
            title: strings.usersAndPermissions.email,
            type: 'email',
            value: email,
            id: 'user-and-permissions-email-input',
            validation: yup.string().email(strings.validations.validEmail),
          }}
          setChange={handleInputChange}
          setValidation={handleValidation}
          isReadOnly={isSubmitting}
          fullWidth
        />
        {isError && (
          <FormHelperText error>{strings.usersAndPermissions.emailNotFound}</FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {strings.actions.cancel}
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          color="primary"
          loading={isSubmitting}
          disabled={!isEmailValid}
        >
          {strings.actions.add}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

AddUserDialog.propTypes = {
  isOpen: P.bool.isRequired,
  onClose: P.func.isRequired,
};
